import React from 'react';
import { C } from '../constants';
import { audience } from '../services/auth';
import { serviciosLocales } from '../services/entitlements';
import { desgloseCierre } from '../services/cierre';
import { girarPagoTecnico, radicarPagoTecnico } from '../services/pagos';

// ─────────────────────────────────────────────────────────────────────────────
// ÓRDENES DE SERVICIO + SOPORTE — migra el modelo WorkOrder de FluxAI al flujo de
// Solar Hub: la cotización GANADA genera una ORDEN DE SERVICIO que el TÉCNICO opera
// (instalación) y que luego lleva SOPORTE post-instalación (tickets/PQR).
//   Ciclo: programada → en_instalación → instalada → en_soporte → cerrada (· cancelada)
//   - Admin/operador: genera desde cotización ganada, asigna técnico, avanza estado,
//     gestiona la cola de soporte.
//   - Técnico: ve SUS órdenes asignadas, avanza la instalación y registra soporte.
// Persistencia client-side (localStorage `al:ordenes`), igual que cotizaciones; el
// backend real (`ordenes` del hub-api) toma el relevo al desplegarse.
// ─────────────────────────────────────────────────────────────────────────────

// Flujo de servicio de alto nivel de la suite Alebas (lógica de negocio del dueño):
//   inspección  → al ASIGNAR un instalador (visita técnica con VALOR de cobro).
//   instalación → cuando el cliente ACEPTA la oferta.
//   mant. preventivo / correctivo → post-venta (con o sin garantía). Si el cliente tiene
//     telemetría de FluxAI contratada, el preventivo pasa a PREDICTIVO (dato real).
const TIPOS = {
  inspeccion:    { label: 'Inspección',          col: C.teal,    flujo: ['programada', 'en_proceso', 'completada'] },
  instalacion:   { label: 'Instalación',         col: C.yellow,  flujo: ['programada', 'en_instalacion', 'instalada', 'en_soporte', 'cerrada'] },
  mant_prev:     { label: 'Mant. preventivo',    col: '#00c781', flujo: ['programada', 'en_proceso', 'resuelta'] },
  mant_corr:     { label: 'Mant. correctivo',    col: C.orange,  flujo: ['programada', 'en_proceso', 'resuelta'] },
};
const flujoDe = (o) => (TIPOS[o.tipo] || TIPOS.instalacion).flujo;
const EST = {
  programada:     { label: 'Programada',      col: C.teal },
  en_proceso:     { label: 'En proceso',      col: C.amber },
  completada:     { label: 'Completada',      col: '#00c781' },
  en_instalacion: { label: 'En instalación',  col: C.amber },
  instalada:      { label: 'Instalada',       col: '#00c781' },
  en_soporte:     { label: 'En soporte',      col: C.orange },
  resuelta:       { label: 'Resuelta',        col: '#00c781' },
  cerrada:        { label: 'Cerrada',         col: C.muted },
  cancelada:      { label: 'Cancelada',       col: '#c0392b' },
};
// ¿El cliente de la orden tiene telemetría de FluxAI contratada? (potencia el mantenimiento)
const clienteConFlux = (o) => {
  try { return serviciosLocales(o.email).some(s => s.includes('monitoreo')); } catch { return false; }
};
const fmtCOP = n => n != null ? '$' + Math.round(n).toLocaleString('es-CO') : '—';
const uuid = () => 'os_' + Math.random().toString(36).slice(2, 10);

export default function Ordenes({ ordenes = [], addOrden, updOrden, quotes = [], installers = [], claims, go, soloSoporte = false, sincro = 'ok' }) {
  const aud = audience(claims);
  const esGestor = aud === 'admin' || aud === 'operator';
  const esTecnico = aud === 'technician';
  const esCliente = aud === 'user';
  const email = (claims?.email || '').toLowerCase().trim();
  const [soporteFor, setSoporteFor] = React.useState(null); // id de orden con form de soporte abierto
  const [soporteText, setSoporteText] = React.useState('');
  const [giroFor, setGiroFor] = React.useState(null);
  const [giroMedio, setGiroMedio] = React.useState('breb');
  const [giroRef, setGiroRef] = React.useState('');
  const [giroMsg, setGiroMsg] = React.useState('');
  const [giroBusy, setGiroBusy] = React.useState(false);
  // 🔴 La orden cuyo giro SÍ se liquidó pero cuya cuenta NO llegó a la gestión. El puente falla
  //    cerrado a propósito, así que sin esto el 80 % quedaba pagado y fuera del ciclo de la
  //    empresa **sin que nadie se enterara**: la pantalla decía «Giro registrado» y punto.
  const [sinRadicar, setSinRadicar] = React.useState(null);

  // El técnico solo ve SUS órdenes (por email o nombre asignado).
  const tid = claims?.technician_id ?? claims?.user_id ?? claims?.id;
  const visibles = esTecnico
    ? ordenes.filter((o) => {
      const em = (o.tecnicoEmail || '').toLowerCase().trim();
      if (email && em === email) return true;
      if (tid != null && o.tecnicoId != null && String(o.tecnicoId) === String(tid)) return true;
      return false;
    })
    : ordenes;
  const lista = soloSoporte ? visibles.filter(o => o.estado === 'en_soporte') : visibles;

  // Cotizaciones sin orden de inspección (agendar visita) y ganadas sin instalación.
  const conInspeccion = new Set(ordenes.filter(o => o.tipo === 'inspeccion').map(o => o.quoteId).filter(Boolean));
  const conInstalacion = new Set(ordenes.filter(o => o.tipo === 'instalacion').map(o => o.quoteId).filter(Boolean));
  const sinInspeccion = quotes.filter(q => !conInspeccion.has(q.id));
  const ganadasSinInstalacion = quotes.filter(q => (q.status === 'ganada' || q.status === 'cerrada') && !conInstalacion.has(q.id));

  // Form de agendamiento de inspección (asignar instalador + valor de cobro).
  const [inspQuote, setInspQuote] = React.useState('');
  const [inspTec, setInspTec] = React.useState('');
  const [inspValor, setInspValor] = React.useState('');
  const baseDesdeQuote = (q) => ({
    quoteId: q.id, cliente: q.name || q.company || '—', email: q.email || '',
    sistema: q.systemType || '—', kwp: q.results?.actKwp ?? null, inversion: q.budget?.tot ?? null,
    direccion: q.address || '', fecha: new Date().toISOString(), soporte: [],
  });
  const crearInspeccion = () => {
    const q = quotes.find(x => String(x.id) === String(inspQuote));
    const t = installers.find(i => String(i.id) === String(inspTec));
    if (!q || !t) return;
    addOrden && addOrden({
      id: uuid(), tipo: 'inspeccion', ...baseDesdeQuote(q),
      valor: parseFloat(inspValor) || 0,
      tecnicoId: t.id, tecnicoNombre: t.name || t.nombre || t.email, tecnicoEmail: t.email || '',
      estado: 'programada', notas: 'Orden de inspección — visita técnica con cobro.',
    });
    setInspQuote(''); setInspTec(''); setInspValor('');
  };
  const crearInstalacion = (q) => addOrden && addOrden({
    id: uuid(), tipo: 'instalacion', ...baseDesdeQuote(q), valor: q.budget?.tot ?? null,
    tecnicoId: null, tecnicoNombre: '', tecnicoEmail: '', estado: 'programada',
    garantia: 'vigente', notas: 'Generada manualmente — falta asignar técnico.',
  });
  // Post-venta: abre una orden de MANTENIMIENTO (preventivo/correctivo) desde una instalación.
  const crearMantenimiento = (o, tipo) => addOrden && addOrden({
    id: uuid(), tipo, quoteId: o.quoteId, cliente: o.cliente, email: o.email,
    sistema: o.sistema, kwp: o.kwp, inversion: o.inversion, valor: null,
    direccion: o.direccion, tecnicoId: null, tecnicoNombre: '', tecnicoEmail: '',
    estado: 'programada', fecha: new Date().toISOString(), garantia: o.garantia || 'vigente',
    notas: tipo === 'mant_prev'
      ? (clienteConFlux(o) ? 'Mantenimiento PREDICTIVO — guiado por telemetría FluxAI.' : 'Mantenimiento preventivo programado.')
      : 'Mantenimiento correctivo — atención de falla.',
    soporte: [],
  });
  const setGarantia = (o, g) => updOrden && updOrden({ ...o, garantia: g });

  const setEstado = (o, estado) => updOrden && updOrden({ ...o, estado });
  const netoDe = (o) => desgloseCierre({ instalacion: Number(o.valor || o.valorInstalacion || 0) }).techEarnings;
  const laborHecha = (o) => o.tipo !== 'inspeccion' && (o.estado === 'instalada' || o.estado === 'cerrada' || o.estado === 'completada' || o.estado === 'en_soporte');
  /** ¿La cuenta llegó a `alebas-gestion`? `radicada` y `ya_radicada` son las dos que valen. */
  const radicoBien = (estado) => estado === 'radicada' || estado === 'ya_radicada';

  const registrarGiro = async (o) => {
    setGiroBusy(true); setGiroMsg(''); setSinRadicar(null);
    try {
      const r = await girarPagoTecnico({ order_id: o.id, medio: giroMedio, payment_ref: giroRef });
      if (r && r.ok) {
        updOrden && updOrden({ ...o, pagado: true, pago_estado: 'pagada', pago_ref: giroRef, pago_medio: giroMedio });
        // 🪤 El giro se liquida acá y la cuenta se radica allá, en dos pasos que pueden
        //    terminar distinto. Decir solo «Giro registrado» cuando el segundo falló es la
        //    forma exacta en que una plata se sale del ciclo sin dejar rastro visible.
        const estado = r.pago && r.pago.gestion_estado;
        if (estado && !radicoBien(estado)) {
          setSinRadicar({ id: o.id, estado, error: (r.pago && r.pago.gestion_error) || '' });
          setGiroMsg(estado === 'no_configurado'
            ? 'El giro quedó registrado, pero la gestión no tiene configurada la llave de la línea: la cuenta no se radicó.'
            : `El giro quedó registrado, pero la cuenta no se radicó en la gestión (${estado}).`);
        } else {
          setGiroMsg('Giro registrado. El técnico ve el 80% como girado.');
          setGiroFor(null); setGiroRef('');
        }
      } else setGiroMsg((r && r.error) || 'No se pudo registrar el giro.');
    } catch (e) {
      setGiroMsg(e.message || 'No se pudo registrar el giro.');
    } finally { setGiroBusy(false); }
  };

  /**
   * REINTENTAR la radicación. La referencia es determinista (`SH-<order_id>`), así que
   * reintentar no duplica: si ya estaba, la gestión responde `ya_radicada`.
   */
  const reintentarRadicacion = async (o) => {
    setGiroBusy(true);
    try {
      const r = await radicarPagoTecnico(o.id);
      if (r && radicoBien(r.estado)) {
        setSinRadicar(null);
        setGiroMsg(`Cuenta radicada en la gestión${r.referencia ? ` · ${r.referencia}` : ''}.`);
        setGiroFor(null); setGiroRef('');
      } else {
        setGiroMsg((r && (r.detalle || r.error)) || `La gestión sigue sin recibirla (${(r && r.estado) || 'sin estado'}).`);
      }
    } catch (e) {
      setGiroMsg(e.message || 'No se pudo radicar.');
    } finally { setGiroBusy(false); }
  };
  const asignarTecnico = (o, t) => updOrden && updOrden({ ...o, tecnicoId: t?.id || null, tecnicoNombre: t?.name || t?.nombre || '', tecnicoEmail: t?.email || '' });
  const addTicket = (o, descripcion) => updOrden && updOrden({
    ...o, estado: 'en_soporte',
    soporte: [...(o.soporte || []), { id: uuid(), fecha: new Date().toISOString(), descripcion, estado: 'abierto' }],
  });
  const cerrarTicket = (o, tid) => updOrden && updOrden({
    ...o, soporte: (o.soporte || []).map(s => s.id === tid ? { ...s, estado: 'cerrado' } : s),
  });

  const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 18px', marginBottom: 14 };
  const lblS = { display: 'block', fontSize: 10, color: C.muted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 };
  const selS = { width: '100%', background: C.dark, border: `1px solid ${C.border}`, borderRadius: 7, padding: '8px 10px', color: C.text, fontSize: 12, boxSizing: 'border-box', cursor: 'pointer' };

  // KPIs
  const k = {
    total: visibles.length,
    instalando: visibles.filter(o => o.estado === 'en_instalacion').length,
    soporte: visibles.filter(o => o.estado === 'en_soporte').length,
    cerradas: visibles.filter(o => o.estado === 'cerrada').length,
  };

  // ── VISTA DEL CLIENTE FINAL ────────────────────────────────────────────────
  // El cliente ve el ESTADO de su(s) instalación(es) y abre/realiza seguimiento de
  // SOPORTE posventa, sin los controles de gestión. Sus órdenes se generan al ACEPTAR
  // la propuesta (App.aceptarCotizacion); además derivamos las cotizaciones aceptadas
  // que aún no tienen orden, para que el servicio nunca quede invisible.
  if (esCliente) {
    const mias = ordenes.filter(o => (o.email || '').toLowerCase().trim() === email);
    const conOrden = new Set(mias.map(o => o.quoteId).filter(Boolean));
    const aceptadas = quotes.filter(q =>
      ['ganada', 'cerrada', 'aceptada'].includes(q.status) &&
      (!email || (q.email || '').toLowerCase().trim() === email) &&
      !conOrden.has(q.id));
    const items = [
      ...mias.map(o => ({ key: o.id, kind: 'orden', o, fecha: o.fecha })),
      ...aceptadas.map(q => ({ key: 'q_' + q.id, kind: 'pendiente', q, fecha: q.cliente_acepto_en || null })),
    ].sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0));

    const pedirSoporte = (item, descripcion) => {
      if (item.kind === 'orden') { addTicket(item.o, descripcion); return; }
      const q = item.q; // cotización aceptada sin orden → crea la orden con el ticket abierto
      addOrden && addOrden({
        id: uuid(), tipo: 'instalacion', ...baseDesdeQuote(q), valor: q.budget?.tot ?? null,
        tecnicoId: null, tecnicoNombre: '', tecnicoEmail: '', estado: 'en_soporte', garantia: 'vigente',
        fecha: new Date().toISOString(), notas: 'Solicitud de soporte del cliente.',
        soporte: [{ id: uuid(), fecha: new Date().toISOString(), descripcion, estado: 'abierto' }],
      });
    };

    // Cada item (orden real o cotización aceptada sin orden) se ve como una orden uniforme.
    const toView = (item) => item.kind === 'orden' ? item.o : ({
      id: item.key, _pendiente: true, quoteId: item.q.id, tipo: 'instalacion',
      cliente: item.q.name || item.q.company || '—', email: item.q.email || email,
      sistema: item.q.systemType || '—', kwp: item.q.results?.actKwp ?? null,
      inversion: item.q.budget?.tot ?? null, valor: item.q.budget?.tot ?? null,
      direccion: item.q.address || '', tecnicoId: null, tecnicoNombre: '', tecnicoEmail: '',
      estado: 'programada', garantia: 'vigente', fecha: item.q.cliente_acepto_en || null, soporte: [],
    });
    const sinTecnico = (o) => !o.tecnicoNombre && !o.tecnicoId;
    const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    // Narrativa: qué pasa AHORA con la orden + próximo paso (modelo de servicio Alebas).
    const narrar = (o) => {
      const t = o.tecnicoNombre;
      const obj = o.tipo === 'inspeccion' ? 'visita técnica' : o.tipo === 'instalacion' ? 'instalación' : 'mantenimiento';
      switch (o.estado) {
        case 'programada':
          return sinTecnico(o)
            ? { ic: '', tit: 'Estamos asignando tu técnico', txt: `Tu orden de ${obj} está registrada y programada. Un coordinador está asignando un técnico certificado (RETIE) para atenderte; te avisaremos la fecha apenas quede asignado (normalmente 1–2 días hábiles).`, sig: 'Asignación de técnico' }
            : { ic: '', tit: 'Técnico asignado', txt: `${t} fue asignado para tu ${obj} y coordinará contigo la fecha de la visita.`, sig: 'Confirmación de fecha' };
        case 'en_proceso':
 return { ic: '', tit: 'En proceso', txt: `${t || 'El equipo técnico'} está atendiendo tu ${obj}.`, sig: 'Cierre del servicio' };
        case 'en_instalacion':
 return { ic: '', tit: 'Instalación en curso', txt: `${t || 'El equipo técnico'} está instalando tu sistema en sitio.`, sig: 'Puesta en marcha' };
        case 'instalada':
          return { ic: '', tit: 'Instalación completada', txt: 'Tu sistema quedó instalado y operativo. Entras en periodo de garantía y posventa.', sig: 'Posventa y monitoreo' };
        case 'completada':
        case 'resuelta':
          return { ic: '', tit: 'Servicio completado', txt: 'Tu servicio fue completado correctamente.', sig: null };
        case 'en_soporte':
          return { ic: '', tit: 'Soporte en curso', txt: 'Tu solicitud está siendo atendida por nuestro equipo. Te responderemos a la brevedad por este medio.', sig: 'Resolución del caso' };
        case 'cerrada':
          return { ic: '', tit: 'Orden cerrada', txt: 'Esta orden fue cerrada. Gracias por confiar en Alebas Ingeniería.', sig: null };
        case 'cancelada':
 return { ic: '⛔', tit: 'Orden cancelada', txt: 'Esta orden fue cancelada. Si crees que es un error, solicita soporte.', sig: null };
        default:
          return { ic: '', tit: 'En gestión', txt: 'Tu orden está en gestión.', sig: null };
      }
    };

    return (
      <div className="al-content" style={{ maxWidth: 980, margin: '0 auto', padding: '20px 22px 40px', width: '100%' }}>
        <div style={{ borderLeft: `3px solid ${C.teal}`, paddingLeft: 12, marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.heading }}>Órdenes y soporte</h1>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>El estado de tu instalación y el soporte posventa. Un ingeniero te acompaña en cada paso.</div>
        </div>

        {items.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', padding: '34px 20px' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}></div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.heading, marginBottom: 4 }}>{sincro === 'fallo' ? 'No se pudo cargar tus órdenes' : sincro === 'pendiente' ? 'Cargando tus órdenes…' : 'Aún no tienes órdenes de servicio'}</div>
            <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, maxWidth: 460, margin: '0 auto 16px' }}>
              {sincro === 'fallo' ? (<>La consulta al servidor no respondió, así que <strong>esto no significa que no tengas órdenes</strong>. Vuelve a intentarlo en unos minutos.</>) : (<>Cuando aceptes una propuesta en <strong>Mis cotizaciones</strong>, aquí verás programada tu instalación y podrás
              solicitar y seguir el <strong>soporte posventa</strong>.</>)}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => go && go('misCotizaciones')} style={{ background: C.teal, color: C.heading, border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 800, fontSize: 12.5, cursor: 'pointer' }}>Ver mis cotizaciones</button>
              <button onClick={() => go && go('quoter')} style={{ background: 'transparent', color: C.teal, border: `1px solid ${C.teal}55`, borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>Cotizar un sistema</button>
            </div>
          </div>
        ) : items.map(item => {
          const v = toView(item);
          const est = EST[v.estado] || EST.programada;
          const flujo = flujoDe(v);
          const tipo = TIPOS[v.tipo] || TIPOS.instalacion;
          const idx = flujo.indexOf(v.estado);
          const abierto = soporteFor === item.key;
          const n = narrar(v);
          const conFlux = (v.tipo === 'mant_prev' || v.tipo === 'mant_corr') && clienteConFlux(v);
          return (
            <div key={item.key} style={{ ...card, borderLeft: `3px solid ${tipo.col}` }}>
              {/* Cabecera */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 9, fontWeight: 800, color: tipo.col, background: `${tipo.col}1a`, border: `1px solid ${tipo.col}55`, borderRadius: 5, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: 0.4 }}>{tipo.label}</span>
                    {v.garantia && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: v.garantia === 'vigente' ? '#00c781' : C.muted, background: `${v.garantia === 'vigente' ? '#00c781' : C.muted}14`, border: `1px solid ${v.garantia === 'vigente' ? '#00c781' : C.muted}44`, borderRadius: 5, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: 0.3 }}>{v.garantia === 'vigente' ? 'En garantía' : 'Sin garantía'}</span>
                    )}
                    {conFlux && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: C.teal, background: `${C.teal}14`, border: `1px solid ${C.teal}44`, borderRadius: 5, padding: '2px 8px', letterSpacing: 0.3 }}>Potenciado por FluxAI</span>
                    )}
                    <span style={{ fontSize: 14, fontWeight: 800, color: C.heading }}>{v.sistema} · {v.kwp ?? '—'} kWp</span>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                    {v._pendiente ? 'Propuesta aceptada' : `Orden #${String(v.id).slice(-6)}`} · {fmtFecha(v.fecha)}
                    {' · '}{v.tipo === 'inspeccion' ? `Inspección: ${fmtCOP(v.valor)}` : `Valor: ${fmtCOP(v.valor ?? v.inversion)}`}
                    {v.direccion ? ` · ${v.direccion}` : ''}
                  </div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: est.col, background: `${est.col}1a`, border: `1px solid ${est.col}55`, borderRadius: 6, padding: '3px 10px', textTransform: 'uppercase', letterSpacing: 0.3 }}>{est.label}</span>
              </div>

              {/* Estado actual (narrado) — qué pasa AHORA + próximo paso */}
              <div style={{ background: C.dark, border: `1px solid ${est.col}44`, borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: C.heading, marginBottom: 4 }}>{n.ic} {n.tit}</div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.55 }}>{n.txt}</div>
                {n.sig && <div style={{ fontSize: 11, color: est.col, fontWeight: 700, marginTop: 8 }}>Próximo paso → {n.sig}</div>}
              </div>

              {/* Técnico asignado / pendiente — responde "¿qué pasa si aún no hay técnico?" */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ fontSize: 11, color: C.muted }}>Técnico:</span>
                {sinTecnico(v)
                  ? <span style={{ fontSize: 11, fontWeight: 700, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}55`, borderRadius: 6, padding: '3px 10px' }}>Asignación pendiente — te avisaremos</span>
                  : <span style={{ fontSize: 12, fontWeight: 700, color: C.heading }}>{v.tecnicoNombre}</span>}
              </div>

              {/* Riel del flujo con etiquetas por paso (dónde va tu orden) */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {flujo.map((f, i) => (
                  <div key={f} style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                    <div style={{ height: 5, borderRadius: 3, background: i <= idx ? est.col : C.border }} />
                    <div style={{ fontSize: 8.5, marginTop: 4, color: i === idx ? est.col : C.muted, fontWeight: i === idx ? 800 : 500, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{EST[f].label}</div>
                  </div>
                ))}
              </div>

              {/* Soporte posventa (cliente) */}
              {!abierto ? (
                <button onClick={() => { setSoporteFor(item.key); setSoporteText(''); }} style={{ background: 'transparent', color: C.orange, border: `1px solid ${C.orange}55`, borderRadius: 7, padding: '7px 13px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>+ Solicitar soporte</button>
              ) : (
                <div style={{ padding: '12px 14px', background: C.dark, border: `1px solid ${C.orange}55`, borderRadius: 9 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.orange, marginBottom: 6 }}>¿En qué necesitas ayuda?</label>
                  <textarea value={soporteText} onChange={e => setSoporteText(e.target.value)} autoFocus rows={2} placeholder="Ej.: quiero agendar la instalación / el inversor muestra una alerta / una duda sobre la propuesta…"
                    style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 11px', color: C.text, fontSize: 12.5, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={() => { const d = soporteText.trim(); if (d) { pedirSoporte(item, d); setSoporteFor(null); setSoporteText(''); } }} disabled={!soporteText.trim()}
                      style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: !soporteText.trim() ? `${C.muted}33` : C.orange, color: !soporteText.trim() ? C.muted : '#1a1205', fontWeight: 800, fontSize: 12, cursor: !soporteText.trim() ? 'default' : 'pointer' }}>Enviar solicitud</button>
                    <button onClick={() => { setSoporteFor(null); setSoporteText(''); }} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
                  </div>
                </div>
              )}

              {/* Historial de soporte (lectura) */}
              {(v.soporte || []).length > 0 && (
                <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Tus solicitudes de soporte</div>
                  {v.soporte.map(s => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
                      <span style={{ color: C.text }}><span style={{ color: C.muted }}>{fmtFecha(s.fecha)} · </span>{s.descripcion}</span>
                      <span style={{ color: s.estado === 'abierto' ? C.orange : '#00c781', fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{s.estado === 'abierto' ? 'En atención' : 'Resuelto'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="al-content" style={{ maxWidth: 1180, margin: '0 auto', padding: '20px 22px 40px', width: '100%' }}>
      <div style={{ borderLeft: `3px solid ${C.teal}`, paddingLeft: 12, marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.heading }}>{soloSoporte ? 'Cola de soporte' : 'Órdenes y soporte'}</h1>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
          {soloSoporte ? 'Órdenes con soporte post-instalación abierto.'
            : esTecnico ? 'Tus órdenes de servicio, instalación y PQR de tus clientes.'
            : 'La cotización ganada genera la orden de servicio: instalación (técnico) y soporte posterior.'}
        </div>
      </div>

      {!soloSoporte && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
          {[['Órdenes', k.total, C.teal], ['En instalación', k.instalando, C.amber], ['En soporte', k.soporte, C.orange], ['Cerradas', k.cerradas, '#00c781']].map(([l, v, col]) => (
            <div key={l} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{l}</div>
              <div style={{ fontSize: 23, fontWeight: 900, color: col, marginTop: 3 }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Agendar INSPECCIÓN (asignar instalador + valor de cobro) — gestor */}
      {esGestor && !soloSoporte && sinInspeccion.length > 0 && installers.length > 0 && (
        <div style={{ ...card, borderColor: `${C.teal}55` }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.heading, marginBottom: 4 }}>Agendar inspección — asigna instalador y valor de cobro</div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>Al asignar un instalador se crea una orden de <strong>inspección</strong> (visita técnica con cobro). Tras la inspección se hace la oferta; si el cliente la acepta, se abre la orden de instalación.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(170px,1.6fr) minmax(150px,1.2fr) minmax(130px,1fr) auto', gap: 8, alignItems: 'end' }}>
            <div>
              <label style={lblS}>Cotización</label>
              <select value={inspQuote} onChange={e => setInspQuote(e.target.value)} style={selS}>
                <option value="">Elige…</option>
                {sinInspeccion.map(q => <option key={q.id} value={q.id}>{(q.name || q.company || '—')} · {q.systemType} · {q.results?.actKwp ?? '—'}kWp</option>)}
              </select>
            </div>
            <div>
              <label style={lblS}>Instalador</label>
              <select value={inspTec} onChange={e => setInspTec(e.target.value)} style={selS}>
                <option value="">Elige…</option>
                {installers.map(i => <option key={i.id} value={i.id}>{i.name || i.nombre || i.email}</option>)}
              </select>
            </div>
            <div>
              <label style={lblS}>Valor inspección (COP)</label>
              <input type="number" value={inspValor} onChange={e => setInspValor(e.target.value)} placeholder="Ej. 150000" style={selS} />
            </div>
            <button onClick={crearInspeccion} disabled={!inspQuote || !inspTec} style={{ padding: '9px 16px', borderRadius: 7, border: 'none', background: (!inspQuote || !inspTec) ? `${C.muted}33` : C.teal, color: (!inspQuote || !inspTec) ? C.muted : C.heading, fontWeight: 700, fontSize: 12, cursor: (!inspQuote || !inspTec) ? 'default' : 'pointer' }}>Crear inspección</button>
          </div>
        </div>
      )}

      {/* Cotizaciones aceptadas sin orden de instalación (respaldo manual) */}
      {esGestor && !soloSoporte && ganadasSinInstalacion.length > 0 && (
        <div style={{ ...card, borderColor: `${C.yellow}55` }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.heading, marginBottom: 8 }}>Aceptadas sin orden de instalación</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ganadasSinInstalacion.map(q => (
              <div key={q.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '8px 10px', background: C.dark, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: C.heading, fontWeight: 600 }}>
                  {q.name || q.company || '—'} <span style={{ color: C.muted, fontWeight: 400 }}>· {q.systemType} · {q.results?.actKwp ?? '—'} kWp · {fmtCOP(q.budget?.tot)}</span>
                </div>
                <button onClick={() => crearInstalacion(q)} style={{ background: C.yellow, color: '#1a1205', border: 'none', borderRadius: 7, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>+ Crear instalación</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de órdenes */}
      {lista.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '34px 18px', color: C.muted, fontSize: 13 }}>
          {sincro === 'fallo'
            ? 'No se pudo consultar las órdenes: la consulta al servidor no respondió. Esto NO significa que no haya órdenes.'
            : sincro === 'pendiente' ? 'Cargando órdenes…'
            : soloSoporte ? 'No hay órdenes en soporte.' : esTecnico ? 'No tienes órdenes asignadas.' : 'Aún no hay órdenes. Genera una desde una cotización ganada.'}
        </div>
      ) : lista.map(o => {
        const est = EST[o.estado] || EST.programada;
        const flujo = flujoDe(o);
        const tipo = TIPOS[o.tipo] || TIPOS.instalacion;
        const idx = flujo.indexOf(o.estado);
        const siguiente = idx >= 0 && idx < flujo.length - 1 ? flujo[idx + 1] : null;
        const ticketsAbiertos = (o.soporte || []).filter(s => s.estado === 'abierto').length;
        return (
          <div key={o.id} style={{ ...card, borderLeft: `3px solid ${tipo.col}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 9, fontWeight: 800, color: tipo.col, background: `${tipo.col}1a`, border: `1px solid ${tipo.col}55`, borderRadius: 5, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: 0.4 }}>{tipo.label}</span>
                  {o.garantia && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: o.garantia === 'vigente' ? '#00c781' : C.muted, background: `${o.garantia === 'vigente' ? '#00c781' : C.muted}14`, border: `1px solid ${o.garantia === 'vigente' ? '#00c781' : C.muted}44`, borderRadius: 5, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: 0.3 }}>{o.garantia === 'vigente' ? 'En garantía' : 'Sin garantía'}</span>
                  )}
                  {(o.tipo === 'mant_prev' || o.tipo === 'mant_corr') && clienteConFlux(o) && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: C.teal, background: `${C.teal}14`, border: `1px solid ${C.teal}44`, borderRadius: 5, padding: '2px 8px', letterSpacing: 0.3 }}>Potenciado por FluxAI</span>
                  )}
                  <span style={{ fontSize: 14, fontWeight: 800, color: C.heading }}>{o.cliente} · {o.sistema} · {o.kwp ?? '—'} kWp</span>
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                  Orden #{o.id.slice(-6)} · {o.fecha ? new Date(o.fecha).toLocaleDateString('es-CO') : '—'}
                  {' · '}{o.tipo === 'inspeccion' ? `Cobro de inspección: ${fmtCOP(o.valor)}` : `Valor: ${fmtCOP(o.valor ?? o.inversion)}`}
                  {o.direccion ? ` · ${o.direccion}` : ''}
                </div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: est.col, background: `${est.col}1a`, border: `1px solid ${est.col}55`, borderRadius: 6, padding: '3px 10px', textTransform: 'uppercase', letterSpacing: 0.3 }}>{est.label}</span>
            </div>

            {/* Técnico asignado */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: C.muted }}>Técnico:</span>
              {esGestor ? (
                <select value={o.tecnicoId || ''} onChange={e => asignarTecnico(o, installers.find(i => String(i.id) === e.target.value))}
                  style={{ background: C.dark, border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 9px', color: C.text, fontSize: 12, cursor: 'pointer' }}>
                  <option value="">— Sin asignar —</option>
                  {installers.map(i => <option key={i.id} value={i.id}>{i.name || i.nombre || i.email}</option>)}
                </select>
              ) : (
                <span style={{ fontSize: 12, color: C.heading, fontWeight: 600 }}>{o.tecnicoNombre || 'Sin asignar'}</span>
              )}
            </div>

            {/* Riel del flujo */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
              {flujo.map((f, i) => (
                <div key={f} title={EST[f].label} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= idx ? est.col : C.border }} />
              ))}
            </div>

            {/* Acciones de estado (gestor o técnico asignado) */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {(esGestor || esTecnico) && siguiente && o.estado !== 'cancelada' && (
                <button onClick={() => setEstado(o, siguiente)} style={{ background: C.teal, color: C.heading, border: 'none', borderRadius: 7, padding: '7px 13px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  Avanzar a {EST[siguiente].label} →
                </button>
              )}
              {(esGestor || esTecnico) && (o.estado === 'instalada' || o.estado === 'en_soporte' || o.estado === 'cerrada') && (
                <button onClick={() => { setSoporteFor(soporteFor === o.id ? null : o.id); setSoporteText(''); }}
                  style={{ background: soporteFor === o.id ? `${C.orange}14` : 'transparent', color: C.orange, border: `1px solid ${C.orange}55`, borderRadius: 7, padding: '7px 13px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  + Registrar soporte
                </button>
              )}
              {/* Post-venta: abrir mantenimiento desde una instalación entregada */}
              {esGestor && o.tipo === 'instalacion' && (o.estado === 'instalada' || o.estado === 'en_soporte' || o.estado === 'cerrada') && (
                <>
                  <button onClick={() => crearMantenimiento(o, 'mant_prev')} title={clienteConFlux(o) ? 'Predictivo con telemetría FluxAI' : 'Preventivo programado'}
                    style={{ background: 'transparent', color: '#00c781', border: '1px solid #00c78155', borderRadius: 7, padding: '7px 13px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    + Mant. preventivo{clienteConFlux(o) ? ' (FluxAI)' : ''}
                  </button>
                  <button onClick={() => crearMantenimiento(o, 'mant_corr')}
                    style={{ background: 'transparent', color: C.orange, border: `1px solid ${C.orange}55`, borderRadius: 7, padding: '7px 13px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    + Mant. correctivo
                  </button>
                </>
              )}
              {/* Garantía (gestor) */}
              {esGestor && o.tipo !== 'inspeccion' && o.garantia && (
                <button onClick={() => setGarantia(o, o.garantia === 'vigente' ? 'vencida' : 'vigente')}
                  style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 7, padding: '7px 11px', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>
                  Garantía: {o.garantia === 'vigente' ? 'vigente' : 'vencida'} ↺
                </button>
              )}
              {esGestor && o.estado !== 'cancelada' && o.estado !== 'cerrada' && (
                <button onClick={() => setEstado(o, 'cancelada')} style={{ background: 'transparent', color: '#c0392b', border: '1px solid #c0392b44', borderRadius: 7, padding: '7px 13px', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
              )}
              {laborHecha(o) && netoDe(o) > 0 && (
                <span style={{ fontSize: 11, color: C.teal, fontWeight: 700 }}>
                  80% del técnico: {fmtCOP(netoDe(o))}{o.pagado || o.pago_estado === 'pagada' ? ' · girado' : ' · listo para giro'}
                </span>
              )}
              {esGestor && laborHecha(o) && netoDe(o) > 0 && !o.pagado && o.pago_estado !== 'pagada' && (
                <button onClick={() => { setGiroFor(giroFor === o.id ? null : o.id); setGiroMsg(''); }}
                  style={{ background: giroFor === o.id ? `${C.teal}14` : 'transparent', color: C.teal, border: `1px solid ${C.teal}55`, borderRadius: 7, padding: '7px 13px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  Registrar giro
                </button>
              )}
              {ticketsAbiertos > 0 && <span style={{ fontSize: 11, color: C.orange, fontWeight: 700 }}>{ticketsAbiertos} soporte(s) abierto(s)</span>}
            </div>

            {giroFor === o.id && (
              <div style={{ marginTop: 10, padding: '12px 14px', background: C.dark, border: `1px solid ${C.teal}55`, borderRadius: 9 }}>
                <div style={{ fontSize: 12, color: C.heading, fontWeight: 700, marginBottom: 6 }}>
                  Girar {fmtCOP(netoDe(o))} al técnico (80% de la mano de obra)
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, lineHeight: 1.4 }}>
                  El cliente ya pagó por Wompi/Rapyd/Bre-B (suite FluxAI). Esto registra el desembolso al técnico, no un cobro nuevo.
                </div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 4 }}>Medio</label>
                <select value={giroMedio} onChange={(e) => setGiroMedio(e.target.value)}
                  style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: '7px 10px', color: C.text, fontSize: 12, marginBottom: 8 }}>
                  <option value="breb">Bre-B</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="wompi">Wompi (mismo comercio FluxAI)</option>
                  <option value="rapyd">Rapyd</option>
                </select>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 4 }}>Referencia del giro</label>
                <input value={giroRef} onChange={(e) => setGiroRef(e.target.value)} placeholder="Nequi / Bre-B / comprobante"
                  style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: '8px 11px', color: C.text, fontSize: 12.5, boxSizing: 'border-box', marginBottom: 8 }} />
                {giroMsg && <div style={{ fontSize: 11, color: giroMsg.startsWith('Giro') ? C.teal : C.orange, marginBottom: 8 }}>{giroMsg}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => registrarGiro(o)} disabled={giroBusy}
                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: C.teal, color: C.heading, fontWeight: 800, fontSize: 12, cursor: giroBusy ? 'default' : 'pointer' }}>
                    {giroBusy ? 'Registrando…' : 'Confirmar giro'}
                  </button>
                  {sinRadicar && sinRadicar.id === o.id && (
                    <button onClick={() => reintentarRadicacion(o)} disabled={giroBusy}
                      style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.orange}`, background: 'transparent', color: C.orange, fontWeight: 800, fontSize: 12, cursor: giroBusy ? 'default' : 'pointer' }}>
                      Radicar en la gestión
                    </button>
                  )}
                  <button onClick={() => { setGiroFor(null); setGiroMsg(''); }}
                    style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
                </div>
              </div>
            )}

            {/* Formato in-app (sin diálogo nativo) para registrar soporte */}
            {soporteFor === o.id && (
              <div style={{ marginTop: 10, padding: '12px 14px', background: C.dark, border: `1px solid ${C.orange}55`, borderRadius: 9 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.orange, marginBottom: 6 }}>Describe el caso de soporte</label>
                <textarea value={soporteText} onChange={e => setSoporteText(e.target.value)} autoFocus rows={2}
                  placeholder="Ej.: el inversor reporta falla de aislamiento…"
                  style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 11px', color: C.text, fontSize: 12.5, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => { const d = soporteText.trim(); if (d) { addTicket(o, d); setSoporteFor(null); setSoporteText(''); } }} disabled={!soporteText.trim()}
                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: !soporteText.trim() ? `${C.muted}33` : C.orange, color: !soporteText.trim() ? C.muted : '#1a1205', fontWeight: 800, fontSize: 12, cursor: !soporteText.trim() ? 'default' : 'pointer' }}>Registrar</button>
                  <button onClick={() => { setSoporteFor(null); setSoporteText(''); }} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
                </div>
              </div>
            )}

            {/* Tickets de soporte */}
            {(o.soporte || []).length > 0 && (
              <div style={{ marginTop: 10, borderTop: `1px solid ${C.border}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {o.soporte.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
                    <span style={{ color: C.text }}>
                      <span style={{ color: C.muted }}>{new Date(s.fecha).toLocaleDateString('es-CO')} · </span>{s.descripcion}
                    </span>
                    {s.estado === 'abierto'
                      ? <button onClick={() => cerrarTicket(o, s.id)} style={{ background: 'transparent', color: '#00c781', border: '1px solid #00c78155', borderRadius: 6, padding: '3px 9px', fontWeight: 700, fontSize: 10, cursor: 'pointer' }}>Cerrar</button>
                      : <span style={{ color: '#00c781', fontWeight: 700, fontSize: 10 }}>RESUELTO</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
